import { viewFetch as fetch } from '../view-as.js';
// A read that never answers, on the admin surfaces (module 10 AUDIT-2026-09-21-admin.md,
// finding 1).
//
// Three of the eleven admin tabs did not finish loading on the live store, and every one of
// them drew a page that said "Reading the connectors…" for ever. `useLoad` and the rails
// panels each have exactly two states, a null result and an error, so a request that never
// returns is drawn exactly like one that is about to arrive: there is nothing to press, no
// way to tell a slow read from a stopped one, and nothing ever says what happened.
//
// Worse than the page: the request kept running on the SERVER after the person navigated
// away. Two such reads, started by opening the Routes tab and left behind, were still
// burning CPU twenty-five minutes later and holding ACCESS SHARE on audit.judgement, which
// failed another session's migration twice on 2026-09-21.
//
// So every GET on these two surfaces carries a deadline. It is a safety net and not the fix
// -- migrations 150 and 151 and the routes query are the fix, and take the three reads from
// 33 s, 20 s and over two minutes to under a second each. The net is here because the next
// slow read will be one nobody predicted, and because a page should say what happened rather
// than spin.
//
// Chosen over the alternatives: a shorter deadline (a backfill's first admin read is
// legitimately seconds, and cutting off a read that was about to answer is worse than
// waiting); no deadline and a spinner with elapsed time (it tells the person the truth and
// still leaves the transaction running on the server); and a retry inside the helper (a read
// this slow must not be asked twice by a machine -- the person decides).
//
// TO BE REPLACED, in part: another session is landing apps/portal/src/live.ts, whose
// useLiveRefresh carries an AbortSignal, refuses to run two reads at once, aborts on unmount
// and re-reads when the window comes back. When it is on main these surfaces should adopt it
// and this module should keep only what that hook does not: the deadline is per request and
// belongs beside the fetch either way. Until then this is not a duplicate of anything that
// exists.

/** How long any admin or rails read may take before it is given up on. */
export const LOAD_DEADLINE_MS = 20_000;

/**
 * A read that passed its deadline. A distinct class because the alternative is what the page
 * showed: `AbortError: signal is aborted without reason`, in the admin's face, which says
 * nothing and reads like a fault in the browser.
 */
export class TookTooLong extends Error {
  readonly seconds: number;
  constructor(seconds: number) {
    super(`Cobrain stopped waiting for this after ${seconds} seconds. The read is still slow, not broken — try again, and if it keeps happening say so.`);
    this.name = 'TookTooLong';
    this.seconds = seconds;
  }
}

/**
 * Fetch and consume the response under one deadline. `outer`, when given, is the caller's own signal (a page
 * leaving, or live.ts's hook), and aborting it aborts the read as well; the deadline still
 * applies on top of it.
 *
 * The returned promise rejects with TookTooLong when the deadline was what stopped it, and
 * with the outer signal's own AbortError when the caller stopped it — the page shows the
 * first and ignores the second, because a read the person walked away from is not a fault.
 */
export async function withDeadline<T>(path: string, init: RequestInit, read: (response: Response) => Promise<T>, outer?: AbortSignal, ms: number = LOAD_DEADLINE_MS): Promise<T> {
  const control = new AbortController();
  let lateness: ReturnType<typeof setTimeout> | undefined;
  let expired = false;
  const relay = () => control.abort();
  // Refused before it starts, rather than started and then aborted. Real fetch rejects on an
  // already-aborted signal, so this changes nothing a browser would do -- but it says so here
  // instead of relying on that, and a stub that only rejects on the abort EVENT (the event
  // having already fired) would otherwise hang for ever. Found by this module's own test.
  if (outer?.aborted) throw new DOMException('the caller had already given up', 'AbortError');
  let rejectStopped: (reason: unknown) => void = () => {};
  const stopped = new Promise<never>((_resolve, reject) => { rejectStopped = reject; });
  const abort = () => rejectStopped(new DOMException('the read was stopped', 'AbortError'));
  try {
    control.signal.addEventListener('abort', abort);
    if (outer) outer.addEventListener('abort', relay);
    lateness = setTimeout(() => { expired = true; control.abort(); }, ms);
    // Headers are not the end of the read. Race the complete operation against cancellation
    // as well: a body consumer must not leave the page waiting after its signal was stopped.
    return await Promise.race([fetch(path, { ...init, signal: control.signal }).then(read), stopped]);
  } catch (e: unknown) {
    // The deadline and the caller both abort the same controller, so the exception is the
    // same one either way; only this flag says which of them did it.
    if (expired) throw new TookTooLong(Math.round(ms / 1000));
    throw e;
  } finally {
    if (lateness !== undefined) clearTimeout(lateness);
    if (outer) outer.removeEventListener('abort', relay);
    control.signal.removeEventListener('abort', abort);
  }
}

/** True when this is the caller giving up rather than anything going wrong: nothing to show. */
export function isTheCallerLeaving(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}
